// src/lib/agent/pageContext.ts
//
// Maps the current URL to a "domain" the Teddy agent adapts to.
// The chat's fetch wrapper reads this and passes it to the
// server on every ChatKit request. The same domain is also used
// to pick the start-screen greeting + suggested prompts so the
// chat opens with context-aware copy ("Need help with imports?"
// on /admin/imports, "Ask about your orders" on /portal, etc.).

export type TeddyDomain =
  | "catalog"   // /catalog, /products, /
  | "orders"    // /portal/orders, /portal
  | "invoiced"  // /admin/invoiced-sales, /admin/invoices
  | "sales"     // /admin/sales
  | "imports"   // /admin/imports
  | "ops"       // /admin/repairs, /admin/quotations, /admin/lending
  | "incentives"// /admin/incentives
  | "settings"  // /admin/settings
  | "general";  // anything else

export function domainForPath(path: string): TeddyDomain {
  if (path.startsWith("/catalog") || path === "/") return "catalog";
  if (path.startsWith("/portal/orders") || path === "/portal") return "orders";
  if (path.startsWith("/admin/invoiced") || path.startsWith("/admin/invoices")) return "invoiced";
  if (path.startsWith("/admin/sales")) return "sales";
  if (path.startsWith("/admin/imports") || path.startsWith("/admin/exports")) return "imports";
  if (path.startsWith("/admin/repairs") || path.startsWith("/admin/quotations")
      || path.startsWith("/admin/lending")) return "ops";
  if (path.startsWith("/admin/incentives")) return "incentives";
  if (path.startsWith("/admin/settings")) return "settings";
  return "general";
}

export interface TeddyGreeting {
  greeting: string;
  prompts: { label: string; prompt: string }[];
}

/** Per-locale greeting copy. The keys are the i18n language code;
 *  missing languages fall back to English. Keep copy short — the
 *  chatkit panel only has room for ~3 lines. */
const I18N_GREETINGS: Record<TeddyDomain, Record<string, TeddyGreeting>> = {
  catalog: {
    en: {
      greeting: "Hi, I'm Teddy — ask me anything about the BENCHMARK catalog.",
      prompts: [
        { label: "Search a brand",     prompt: "Show me the products from brand Chief" },
        { label: "Find a projector",    prompt: "What video projectors do we carry?" },
        { label: "Product details",     prompt: "Tell me more about product 53585" },
      ],
    },
    fr: {
      greeting: "Bonjour, je suis Teddy — posez-moi vos questions sur le catalogue BENCHMARK.",
      prompts: [
        { label: "Chercher une marque", prompt: "Montre-moi les produits de la marque Chief" },
        { label: "Trouver un projecteur", prompt: "Quels vidéoprojecteurs proposons-nous ?" },
        { label: "Détails produit",      prompt: "Dis-m'en plus sur le produit 53585" },
      ],
    },
    nl: {
      greeting: "Hallo, ik ben Teddy — stel me vragen over de BENCHMARK-catalogus.",
      prompts: [
        { label: "Zoek een merk",       prompt: "Toon de producten van het merk Chief" },
        { label: "Vind een projector",  prompt: "Welke videoprojectoren voeren wij?" },
        { label: "Productdetails",      prompt: "Vertel meer over product 53585" },
      ],
    },
  },
  orders: {
    en: {
      greeting: "I can help you check your orders. What's the order number?",
      prompts: [
        { label: "List my orders",   prompt: "What are my recent orders?" },
        { label: "Track shipment",   prompt: "Where is my order?" },
        { label: "Order history",    prompt: "Show me the history of order SHOP-2026-0001" },
      ],
    },
    fr: {
      greeting: "Je peux vous aider à suivre vos commandes. Quel est le numéro de commande ?",
      prompts: [
        { label: "Lister mes commandes", prompt: "Quelles sont mes dernières commandes ?" },
        { label: "Suivre une livraison", prompt: "Où en est ma commande ?" },
        { label: "Historique de commande", prompt: "Affiche l'historique de SHOP-2026-0001" },
      ],
    },
    nl: {
      greeting: "Ik kan je helpen met je bestellingen. Wat is het bestelnummer?",
      prompts: [
        { label: "Mijn bestellingen",   prompt: "Wat zijn mijn recente bestellingen?" },
        { label: "Verzending volgen",   prompt: "Waar is mijn bestelling?" },
        { label: "Bestelgeschiedenis",   prompt: "Toon de geschiedenis van SHOP-2026-0001" },
      ],
    },
  },
  invoiced: {
    en: {
      greeting: "I can show you the invoiced sales ledger. What are you looking for?",
      prompts: [
        { label: "Recent invoices",   prompt: "Show the most recent 10 invoices" },
        { label: "Lines for an invoice", prompt: "Get the line items for invoice INV 10-2026-274" },
        { label: "Reconcile",         prompt: "What does the recon report say?" },
      ],
    },
    fr: {
      greeting: "Je peux vous montrer le journal des ventes facturées. Que cherchez-vous ?",
      prompts: [
        { label: "Factures récentes",  prompt: "Affiche les 10 dernières factures" },
        { label: "Lignes d'une facture", prompt: "Liste les lignes de la facture INV 10-2026-274" },
        { label: "Réconciliation",    prompt: "Que dit le rapport de réconciliation ?" },
      ],
    },
    nl: {
      greeting: "Ik kan de gefactureerde verkoopgegevens tonen. Waar zoek je naar?",
      prompts: [
        { label: "Recente facturen",  prompt: "Toon de 10 recentste facturen" },
        { label: "Factuurregels",     prompt: "Toon de regels van factuur INV 10-2026-274" },
        { label: "Reconciliatie",     prompt: "Wat zegt het reconciliatierapport?" },
      ],
    },
  },
  sales: {
    en: {
      greeting: "I can crunch the sales numbers. Try one of these:",
      prompts: [
        { label: "Top customers",     prompt: "Show the top 10 customers for 2025" },
        { label: "Brand split",       prompt: "Revenue by brand for 2026" },
        { label: "Monthly trend",     prompt: "Monthly revenue trend for 2025" },
      ],
    },
    fr: {
      greeting: "Je peux analyser les chiffres de vente. Essayez :",
      prompts: [
        { label: "Meilleurs clients", prompt: "Affiche les 10 meilleurs clients de 2025" },
        { label: "Répartition par marque", prompt: "Chiffre d'affaires par marque en 2026" },
        { label: "Tendance mensuelle", prompt: "Tendance mensuelle du CA en 2025" },
      ],
    },
    nl: {
      greeting: "Ik kan de verkoopcijfers analyseren. Probeer:",
      prompts: [
        { label: "Topklanten",       prompt: "Toon de top 10 klanten van 2025" },
        { label: "Per merk",         prompt: "Omzet per merk in 2026" },
        { label: "Maandelijkse trend", prompt: "Maandelijkse omzettrend in 2025" },
      ],
    },
  },
  imports: {
    en: {
      greeting: "I can help with FileMaker imports. I won't run them for you — open /admin/imports to click the right button.",
      prompts: [
        { label: "What batches are pending?",  prompt: "What FileMaker batches are waiting to be imported?" },
        { label: "Recent apply history",       prompt: "Show the last 5 import runs" },
        { label: "How does the diff work?",    prompt: "How does the delta preview decide new vs updated?" },
      ],
    },
    fr: {
      greeting: "Je peux aider avec les imports FileMaker. Je ne les lance pas pour vous — ouvrez /admin/imports pour cliquer.",
      prompts: [
        { label: "Lots en attente",           prompt: "Quels lots FileMaker attendent d'être importés ?" },
        { label: "Historique d'application",  prompt: "Affiche les 5 derniers imports" },
        { label: "Comment fonctionne le diff ?", prompt: "Comment l'aperçu delta décide nouveau vs mis à jour ?" },
      ],
    },
    nl: {
      greeting: "Ik kan helpen met FileMaker-imports. Ik voer ze niet voor je uit — open /admin/imports om te klikken.",
      prompts: [
        { label: "Wachtende batches",         prompt: "Welke FileMaker-batches wachten op import?" },
        { label: "Recente importgeschiedenis", prompt: "Toon de laatste 5 import-runs" },
        { label: "Hoe werkt de diff?",         prompt: "Hoe beslist de delta-preview nieuw vs bijgewerkt?" },
      ],
    },
  },
  ops: {
    en: {
      greeting: "I can show repairs, quotations and lending for the day-to-day operations.",
      prompts: [
        { label: "Open repairs",      prompt: "What repairs are currently open?" },
        { label: "Active quotations", prompt: "Show active quotations" },
        { label: "Lending out",       prompt: "What demo material is out on lending?" },
      ],
    },
    fr: {
      greeting: "Je peux montrer les réparations, devis et prêts de matériel pour les opérations courantes.",
      prompts: [
        { label: "Réparations ouvertes", prompt: "Quelles réparations sont ouvertes ?" },
        { label: "Devis actifs",          prompt: "Affiche les devis actifs" },
        { label: "Prêts en cours",        prompt: "Quel matériel de démo est prêté ?" },
      ],
    },
    nl: {
      greeting: "Ik kan reparaties, offertes en uitleen tonen voor de dagelijkse operatie.",
      prompts: [
        { label: "Open reparaties",     prompt: "Welke reparaties zijn open?" },
        { label: "Actieve offertes",    prompt: "Toon de actieve offertes" },
        { label: "Uitgeleend",          prompt: "Welk demomateriaal is uitgeleend?" },
      ],
    },
  },
  incentives: {
    en: {
      greeting: "I can walk you through the incentive plans and the commission for a salesperson.",
      prompts: [
        { label: "Active plans",       prompt: "What incentive plans are currently active?" },
        { label: "My commission",      prompt: "What commission would I get for 500k of sales this year?" },
        { label: "Plan lifecycle",     prompt: "Explain the plan lifecycle (draft → legal → management → active)" },
      ],
    },
    fr: {
      greeting: "Je peux vous guider à travers les plans d'incitation et la commission d'un commercial.",
      prompts: [
        { label: "Plans actifs",        prompt: "Quels plans d'incitation sont actifs en ce moment ?" },
        { label: "Ma commission",       prompt: "Quelle commission aurais-je pour 500k de ventes cette année ?" },
        { label: "Cycle de vie du plan", prompt: "Explique le cycle (brouillon → juridique → management → actif)" },
      ],
    },
    nl: {
      greeting: "Ik kan je door de incentivoplannen en de commissie van een verkoper leiden.",
      prompts: [
        { label: "Actieve plannen",    prompt: "Welke incentivoplannen zijn nu actief?" },
        { label: "Mijn commissie",     prompt: "Welke commissie krijg ik voor 500k omzet dit jaar?" },
        { label: "Plan-levenscyclus",  prompt: "Leg de levenscyclus uit (ontwerp → juridisch → management → actief)" },
      ],
    },
  },
  settings: {
    en: {
      greeting: "I can help with the admin settings. What's the question?",
      prompts: [
        { label: "Public Teddy",       prompt: "How do I enable the public Teddy for anonymous visitors?" },
        { label: "Notifications",      prompt: "Who gets notified when an import finishes?" },
        { label: "BFF login",          prompt: "How do I rotate the BFF client secret?" },
      ],
    },
    fr: {
      greeting: "Je peux aider avec les paramètres admin. Quelle est la question ?",
      prompts: [
        { label: "Teddy public",       prompt: "Comment activer le Teddy public pour les visiteurs anonymes ?" },
        { label: "Notifications",      prompt: "Qui est prévenu quand un import se termine ?" },
        { label: "Connexion BFF",      prompt: "Comment faire tourner le secret client BFF ?" },
      ],
    },
    nl: {
      greeting: "Ik kan helpen met de admin-instellingen. Wat is de vraag?",
      prompts: [
        { label: "Publieke Teddy",     prompt: "Hoe activeer ik de publieke Teddy voor anonieme bezoekers?" },
        { label: "Meldingen",          prompt: "Wie krijgt een melding wanneer een import klaar is?" },
        { label: "BFF-login",          prompt: "Hoe roteer ik het BFF-clientgeheim?" },
      ],
    },
  },
  general: {
    en: {
      greeting: "Hi, I'm Teddy — ask me anything about the BENCHMARK catalog (EN/FR/NL).",
      prompts: [
        { label: "Search a brand",    prompt: "Show me the products from brand Chief" },
        { label: "Best sellers",       prompt: "What are the top-selling products?" },
        { label: "What's in stock",    prompt: "What new products did we add this month?" },
      ],
    },
    fr: {
      greeting: "Bonjour, je suis Teddy — posez-moi vos questions sur le catalogue BENCHMARK (EN/FR/NL).",
      prompts: [
        { label: "Chercher une marque", prompt: "Montre-moi les produits de la marque Chief" },
        { label: "Meilleures ventes",   prompt: "Quels sont les produits les plus vendus ?" },
        { label: "Nouveautés",          prompt: "Quels nouveaux produits ce mois-ci ?" },
      ],
    },
    nl: {
      greeting: "Hallo, ik ben Teddy — stel me vragen over de BENCHMARK-catalogus (EN/FR/NL).",
      prompts: [
        { label: "Zoek een merk",      prompt: "Toon de producten van het merk Chief" },
        { label: "Bestsellers",        prompt: "Wat zijn de best verkopende producten?" },
        { label: "Nieuwe producten",   prompt: "Welke nieuwe producten zijn er deze maand?" },
      ],
    },
  },
};

const FALLBACK_LANG = "en";

/**
 * Pick the start-screen greeting + suggested prompts for the
 * current pathname. The function falls back through: requested
 * language → English → general/English. Always returns a
 * non-empty greeting.
 */
export function greetingForPath(path: string, lang: string): TeddyGreeting {
  const domain = domainForPath(path);
  const table = I18N_GREETINGS[domain];
  return (
    table[lang] ??
    table[FALLBACK_LANG] ?? // TS can't prove this is defined
    I18N_GREETINGS.general[FALLBACK_LANG]! // neither can this; but
    // the I18N_GREETINGS table above is the source of truth and
    // every key is filled in by hand.
  );
}
